import catalogJson from './catalog.json';

export type IconLayer = 'transport' | 'poi' | 'landmark' | 'traffic' | 'structure';
export type IconTier = 'standard' | 'landmark';
export type IconStatus = 'approved' | 'placeholder' | 'draft';

export type IconTheme = 'standard' | 'dark';

export interface CatalogEntry {
  id: string;
  file: string;
  layer: IconLayer;
  tier: IconTier;
  label: string;
  status: IconStatus;
  stroke: string;
  fill: string;
  mapLayer: string;
  makiFallback?: string;
  tileTypes?: string[];
  /** Registry source_feature_type values (water, structure, …) for non-POI landmarks. */
  sourceFeatureTypes?: string[];
  /** When true, icon is baked into the style sprite sheet — do not addImage/removeImage. */
  sprite?: boolean;
}

export interface IconTierSpec {
  viewBox: number;
  outerR: number;
  innerR: number;
  strokeWidth: number;
  mapIconSize: number[];
}

export interface IconCatalog {
  version: number;
  tiers: Record<IconTier, IconTierSpec>;
  themes?: {
    dark?: {
      poi?: { path: string; outerFill?: string };
    };
  };
  layers: Record<
    IconLayer,
    {
      label: string;
      path: string;
      entries: CatalogEntry[];
    }
  >;
}

export const ICON_CATALOG = catalogJson as IconCatalog;

export function catalogEntries(layer: IconLayer): CatalogEntry[] {
  return ICON_CATALOG.layers[layer]?.entries ?? [];
}

export function catalogLayerPath(layer: IconLayer, theme: IconTheme = 'standard'): string {
  if (theme === 'dark' && layer === 'poi') {
    return ICON_CATALOG.themes?.dark?.poi?.path ?? '/icons/poi-dark';
  }
  return ICON_CATALOG.layers[layer]?.path ?? `/icons/${layer}`;
}

export function tierSpec(tier: IconTier): IconTierSpec {
  return ICON_CATALOG.tiers[tier];
}

export function allCatalogIconIds(): string[] {
  return (Object.keys(ICON_CATALOG.layers) as IconLayer[]).flatMap((layer) =>
    catalogEntries(layer).map((e) => e.id),
  );
}

/** Runtime addImage ids only — excludes sprite-sheet entries (e.g. traffic). */
export function runtimeCatalogIconIds(): string[] {
  return (Object.keys(ICON_CATALOG.layers) as IconLayer[]).flatMap((layer) =>
    catalogEntries(layer)
      .filter((e) => !e.sprite)
      .map((e) => e.id),
  );
}

export function approvedCatalogEntries(layer: IconLayer): CatalogEntry[] {
  return catalogEntries(layer).filter((e) => e.status === 'approved');
}

export function findCatalogEntryById(id: string): CatalogEntry | undefined {
  for (const layer of Object.keys(ICON_CATALOG.layers) as IconLayer[]) {
    const entry = catalogEntries(layer).find((e) => e.id === id);
    if (entry) return entry;
  }
  return undefined;
}

export function entriesByMakiFallback(layer: IconLayer): Map<string, CatalogEntry> {
  const map = new Map<string, CatalogEntry>();
  for (const entry of catalogEntries(layer)) {
    if (entry.makiFallback) map.set(entry.makiFallback, entry);
  }
  return map;
}
